import {
  Button,
  Divider,
  styled,
  TextField,
  Typography,
  ButtonProps,
  Box,
  InputLabel,
  BoxProps,
  CardProps,
  Card,
  Toolbar,
  AppBar,
  Link as MuiLink,
} from "@mui/material";
import * as React from "react";
import { useContext, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Urls } from "../domain/urls";
import { CoiledLogo } from "../icons/CoiledLogoMark";
import { REDIRECT_URI_PARAM } from "./constants";
import { PageContext } from "../components/layout/page";

export const Text = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: theme.typography.fontWeightLight,
  padding: "8px 16px 0 16px",
}));

export const ExternalLink = styled("a")(({ theme }) => ({
  color: theme.palette.primary.main,
  "&:hover, &:focus": {
    color: theme.palette.secondary.dark,
  },
}));

export const InternalLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  "&:hover, &:focus": {
    color: theme.palette.secondary.dark,
  },
}));

export const BackToSignInButton = styled(Button)({
  paddingTop: "16px",
  display: "flex",
});

export const ErrorMessage = styled(Typography)({
  marginTop: "16px",
});

export const StyledAuthForm = styled("form")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
});

export const AuthForm = styled("form")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "space-evenly",
  width: "100%",
  minHeight: "100px",
  paddingTop: "24px",
  gap: "8px",
});

export const ColumnDiv = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
  alignItems: "center",
  textAlign: "left",
  width: "100%",
  minHeight: "200px",
});

export const HalfDivider = styled(Divider)(({ theme }) => ({
  fontSize: "12px",
  width: "50%",
  fontWeight: 450,
  backgroundColor: "transparent",
  border: "none",
}));

export const InputText = styled(TextField)(({ theme }) => ({
  fontSize: "15px",
  fontWeight: 300,
  lineHeight: "20px",
}));

interface LabelProps {
  children: React.ReactNode;
  htmlFor: string;
}

export const Label = ({ children, htmlFor }: LabelProps): JSX.Element => {
  return (
    <Box display="flex" justifyContent="flex-start" width="100%">
      <InputLabel htmlFor={htmlFor}>{children}</InputLabel>
    </Box>
  );
};

interface ThirdPartyButtonProps extends ButtonProps {
  icon: React.ReactNode;
  href: string;
  children: React.ReactNode;
  onClick?: () => void;
}

export const ThirdPartyButton = ({
  icon,
  href,
  children,
  onClick,
}: ThirdPartyButtonProps): JSX.Element => {
  return (
    <Button
      color="primary"
      variant="outlined"
      startIcon={icon}
      onClick={onClick}
      href={href}
      fullWidth
    >
      {children}
    </Button>
  );
};

interface GroupProps extends BoxProps {
  children: React.ReactNode;
}

export const Group = ({ children, ...props }: GroupProps): JSX.Element => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="8px"
      textAlign="center"
      width="100%"
      {...props}
    >
      {children}
    </Box>
  );
};

interface SectionCardProps extends CardProps {
  children: React.ReactNode;
}

export const SectionCard = ({
  children,
  ...props
}: SectionCardProps): JSX.Element => {
  return (
    <Card
      sx={{
        display: "flex",
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "40px",
        minWidth: "312px",
        paddingY: "20px",
      }}
      {...props}
    >
      {children}
    </Card>
  );
};

export const Header = (): React.ReactElement => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isLoginPage = location.pathname === `/${Urls.Login}`;

  // Preserve redirect_uri parameter when navigating between login and signup
  const redirectUri = searchParams.get(REDIRECT_URI_PARAM);
  const signUpUrl = redirectUri
    ? `/${Urls.SignUp}?${REDIRECT_URI_PARAM}=${encodeURIComponent(redirectUri)}`
    : `/${Urls.SignUp}`;
  const loginUrl = redirectUri
    ? `/${Urls.Login}?${REDIRECT_URI_PARAM}=${encodeURIComponent(redirectUri)}`
    : `/${Urls.Login}`;

  return (
    <AppBar
      position="absolute"
      sx={{
        backgroundColor: "transparent",
        backgroundImage: "none",
        boxShadow: "none",
        paddingX: { xs: 2, md: 1 },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          padding: { xs: 0, md: "0 24px" },
          width: "100%",
          maxWidth: "1088px",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <Link to={Urls.Root} style={{ textDecoration: "none" }}>
          <Box display="flex" alignItems="center" gap="8px">
            <CoiledLogo sx={{ color: "white", height: "30.55px" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "white",
                fontSize: "18px",
                lineHeight: "28px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Coiled
            </Typography>
          </Box>
        </Link>
        <Button
          variant="contained"
          color="secondary"
          disableElevation
          component={MuiLink}
          href={isLoginPage ? signUpUrl : loginUrl}
        >
          {isLoginPage ? "Sign Up" : "Login"}
        </Button>
      </Toolbar>
    </AppBar>
  );
};

export const AuthPage = ({
  children,
  title,
  breadCrumbs,
  docsUrl,
}: {
  children?: React.ReactNode;
  title: string;
  breadCrumbs: any[];
  docsUrl?: string;
}): React.ReactElement => {
  const { setBreadCrumbs, setTitle, setDocsUrl } = useContext(PageContext);

  useEffect(() => {
    setBreadCrumbs(breadCrumbs);
    setTitle(title);
    setDocsUrl(docsUrl);
  }, [title, breadCrumbs, setBreadCrumbs, setTitle, setDocsUrl, docsUrl]);

  return <>{children}</>;
};
